<template>
  <PForm
    ref="pForm"
    :heading="addAddressFormState?.edit ? 'Edit Address' : 'Add Address'"
    :primary-cta="addButton"
    :secondary-cta="{}"
    @close="onClose"
    @submit.prevent="onSubmit"
  >
    <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
      <div v-if="!!props.primaryAddress.address_line" class="col-12">
        <PCheckbox
          v-model="formData.is_primary"
          label="Fill from primary address"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.pincode"
          :validations="{ length: 6, type: 'integer' }"
          maxlength="6"
          label="Pincode"
          :loading="isPincodeLoading"
          placeholder="Enter pincode"
          required
          :disable="formData.is_primary"
          @update:model-value="onUpdatePincode"
        />
      </div>
      <div class="col-6">
        <PSelectInput
          v-model="formData.city"
          :options="unref(cityFilter.entities)"
          :loading="citiesLoading"
          label="City"
          option-label="name"
          option-value="name"
          placeholder="Enter city"
          hint="Press enter to add new city"
          required
          :disable="formData.is_primary"
          new-value-mode="add-unique"
          @new-value="onCreateCity"
          @filter="cityFilter.onFilter"
          @filter-abort="cityFilter.onFilterAbort"
        />
      </div>
      <div class="col-12">
        <PSelectInput
          v-model="computedStateCode"
          :options="unref(stateFilter.entities)"
          label="State"
          option-label="name"
          option-value="state_code"
          placeholder="Select state"
          required
          :disable="formData.is_primary"
          @filter="stateFilter.onFilter"
          @filter-abort="stateFilter.onFilterAbort"
          @update:model-value="onUpdateState"
        />
      </div>
    </div>
    <div class="col-12">
      <PInput
        v-model="computedAddress"
        :validations="{
          label: 'Address',
          minLength: 3,
          maxLength: 100,
          required: true,
        }"
        :disable="formData.is_primary"
        label="Address Line 1"
        placeholder="Enter your detail address, e.g. place name, street, etc."
        type="textarea"
        autogrow
        required
      />
    </div>
    <div class="row q-col-gutter-xl">
      <div class="col-12">
        <PField v-model="computedAddressType" required label="Address Type">
          <template #control>
            <POptionGroup
              v-model="computedAddressType"
              color="primary-100"
              size="md"
              required
              keep-color
              :options="addressTypeOptions"
            />
          </template>
        </PField>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { ref, unref, watch } from "vue";
import addAddressForm from "../models/addAddressForm";
import { extend } from "quasar";
const pForm = ref(null);
const { toggleAddAddressForm, postAddress, putAddress } = useAddressStore();
const { addAddressFormState } = storeToRefs(useAddressStore());

import { getPincodeDetails } from "src/api/address";
import { upperFirstFunc } from "src/assets/scripts/utilities/helpers";

defineExpose({ pForm });

const { states, stateFilter } = useStateFilter();

let { cities, cityFilter, citiesLoading, initCityFilter } = useCityFilter();

const props = defineProps({
  primaryAddress: {
    type: Object,
    default: () => ({
      state: "",
      city: "",
      address_line: "",
    }),
  },
  editAddress: {
    type: Object,
    default: () => ({
      _id: "",
      state: "",
      city: "",
      address_line: "",
    }),
  },
  updateParent: {
    type: Function,
    default: () => () => {
      console.log("Default updateParent function");
    },
  },
  documentType: {
    type: String,
    default: () => null,
  },
  userId: {
    type: String,
    default: "",
  },
  addressType: {
    type: String,
    default: "",
  },
});

const editData = computed(() => props.editAddress);
const { formData, closeForm, isLoading, submitForm } = useForm({
  formModel: addAddressForm({
    type: props.addressType,
    type_display: upperFirstFunc(props.addressType),
  }),
  formRef: pForm,
  entity: editData,
  isForm: addAddressFormState,
  post: postAddress,
  put: putAddress,
  toggleForm: toggleAddAddressForm,
});

const addButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Submit",
  };
});

const computedStateCode = computed({
  get: () => {
    const stateCd = unref(formData).state_code;

    if (stateCd) return stateCd;
    else return unref(formData).state_cd;
  },
  set: (value) => {
    unref(formData).state_code = value;

    unref(formData).state_cd = value;
  },
});

const addressTypeOptions = computed(() => {
  if (props.documentType === "Purchase Order") {
    return [
      { label: "Billing", value: "billing" },
      { label: "Shipping", value: "shipping" },
    ];
  } else if (props.documentType === "Purchase Bill") {
    return [{ label: "Shipping", value: "shipping" }];
  } else if (props.documentType === "E-Way Bill") {
    return [
      { label: "Dispatch", value: "dispatch" },
      { label: "Billing", value: "billing", disable: true },
      { label: "Shipping", value: "shipping", disable: true },
    ];
  } else {
    return [
      { label: "Dispatch", value: "dispatch" },
      { label: "Billing", value: "billing" },
      { label: "Shipping", value: "shipping" },
    ];
  }
});

const computedAddressType = computed({
  get: () => {
    return unref(formData).type || addressTypeOptions?.value?.[0]?.value;
  },
  set: (value) => {
    unref(formData).type = value;
  },
});

const isChangedThroughPincode = ref(false);
const isPincodeLoading = ref(false);
const onUpdatePincode = async () => {
  const enteredPincode = unref(formData).pincode;

  if (enteredPincode.length == 6 && !Number.isNaN(Number(enteredPincode))) {
    try {
      isPincodeLoading.value = true;

      const response = await getPincodeDetails(enteredPincode);

      isChangedThroughPincode.value = true;

      unref(formData)[`state_code`] = unref(states).find(
        (state) => state.name === response?.data?.[0]?.PostOffice?.[0]?.State,
      )?.state_code;

      computedStateCode.value = unref(formData).state_code;

      unref(formData)[`city`] = response?.data?.[0]?.PostOffice?.[0]?.District;

      unref(formData)[`country`] =
        response?.data?.[0]?.PostOffice?.[0]?.Country;

      isPincodeLoading.value = false;
    } catch {
      isPincodeLoading.value = false;

      isChangedThroughPincode.value = false;
    }
  }
};

const onUpdateState = async () => {
  if (unref(formData).state_code) {
    initCityFilter(unref(formData).state_code);
  }
};

watch(
  () => props.editAddress,
  (data) => {
    formData.value = data;

    unref(formData).is_primary = false;
  },
);

const primaryAddress = computed(() => props?.primaryAddress);

watch(
  () => unref(formData).is_primary,
  (value) => {
    if (value) {
      const clonedPrimaryAddress = extend(true, {}, primaryAddress.value);

      formData.value = clonedPrimaryAddress;

      unref(formData).type_display = "Dispatch";

      unref(formData).type = "dispatch";

      unref(formData).state_code = "";

      unref(formData).city = "";

      formData.value.is_primary = value;

      onUpdatePincode();
    }
  },
);

watch(
  () => unref(formData).type,
  (data) => {
    if (!data) {
      unref(formData).type_display = "Dispatch";

      unref(formData).type = "dispatch";

      return;
    }

    unref(formData).type_display = upperFirstFunc(data);
  },
  { immediate: true },
);

const computedAddress = computed({
  get: () => {
    const address = Array.isArray(unref(formData).address)
      ? unref(formData).address[0]
      : unref(formData).address;

    if (address) return address;
    else return unref(formData).address_line;
  },
  set: (value) => {
    unref(formData).address_line = value;

    unref(formData).address = value;
  },
});

const onCreateCity = (city, done) => {
  done(city);

  unref(formData).city = city;

  cities.value.push({ name: city, _id: null });

  cityFilter = useFilter({
    entities: cities,
    entity: "city",
  });
};

const onClose = () => {
  closeForm(true);

  cities.value = [];

  cityFilter.value = {};
};

const onSubmit = async () => {
  unref(formData).address_line = Array.isArray(unref(formData).address_line)
    ? unref(formData).address_line[0]
    : unref(formData).address_line;

  const submission = await submitForm(false);

  props.updateParent(submission?.data?.data);

  closeForm(true);

  cities.value = [];

  cityFilter.value = {};
};

watch(
  () => unref(formData).state_code,
  (newState, previousState) => {
    if (previousState && newState && previousState !== newState) {
      if (!isChangedThroughPincode.value) {
        unref(formData).city = "";
      }

      initCityFilter(newState);
    }

    isChangedThroughPincode.value = false;
  },
);

watchEffect(() => {
  if (props?.userId) {
    unref(formData).user_id = props.userId;
  }
});

watchEffect(() => {
  if (props?.addressType) {
    unref(formData).type = props.addressType;

    unref(formData).type_display = upperFirstFunc(props.addressType);
  }
});
</script>
